import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { Button, ConfirmDialog, PageHeader, SelectField, Spinner } from '../components/ui';
import { adminEventService, type EventOrganizer } from '../services/adminEventService';
import type { EventOrganizerRole, WorkspaceMember } from '../services/workspaceService';

export default function EventAccessPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [organizers, setOrganizers] = useState<EventOrganizer[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [role, setRole] = useState<EventOrganizerRole>('viewer');
  const [memberId, setMemberId] = useState('');
  const [target, setTarget] = useState<EventOrganizer | null>(null);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);

  const inheritedAdmins = useMemo(
    () => members.filter((member) => member.role === 'admin'),
    [members],
  );
  const assignableMembers = useMemo(
    () => members.filter((member) => member.role === 'member'),
    [members],
  );

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const event = await adminEventService.get(eventId);
      const admin = event.effective_role === 'admin';
      setCanEdit(admin);
      if (!admin) {
        setOrganizers([]);
        setMembers([]);
        return;
      }
      const [assigned, eligible] = await Promise.all([
        adminEventService.listOrganizers(eventId),
        adminEventService.listEligibleOrganizers(eventId),
      ]);
      setOrganizers(assigned);
      setMembers(eligible);
    } catch (error) {
      toast.error('Could not load event access', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [eventId]);

  const save = async () => {
    if (!eventId || !memberId) return;
    try {
      await adminEventService.saveOrganizer(eventId, memberId, role);
      setMemberId('');
      await load();
    } catch (error) {
      toast.error('Could not save access', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const remove = async () => {
    if (!eventId || !target) return;
    try {
      await adminEventService.removeOrganizer(eventId, target.user_id);
      setTarget(null);
      await load();
    } catch (error) {
      toast.error('Could not remove access', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  return <div className="p-8 max-w-4xl">
    <PageHeader
      title="Event access"
      description="Workspace admins inherit access. Delegate this event to workspace members as admins, managers, or viewers."
      breadcrumb={<Link to="/events" className="hover:text-gray-300">Events</Link>}
    />
    {loading ? <Spinner /> : !canEdit ? (
      <p className="rounded-xl border border-gray-800 bg-gray-900 p-4 text-sm text-gray-400">
        Only event admins can manage collaborators.
      </p>
    ) : <>
      <section className="mb-5 rounded-2xl border border-sky-500/20 bg-sky-500/5 p-4">
        <h2 className="text-sm font-semibold text-white">Inherited workspace admins</h2>
        <p className="mt-1 text-xs text-gray-400">These people administer every event in this workspace. Their access is managed from Workspace team.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {inheritedAdmins.map((member) => (
            <span key={member.user_id} className="rounded-full border border-sky-500/20 bg-gray-950 px-3 py-1 text-xs text-gray-300">
              {member.name} · Admin (inherited)
            </span>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-[1fr_160px_auto] gap-3 rounded-2xl border border-gray-800 bg-gray-900/70 p-4 mb-5">
        <SelectField
          aria-label="Workspace member"
          value={memberId}
          onChange={(event) => setMemberId(event.target.value)}
          options={[
            { value: '', label: 'Choose a workspace member' },
            ...assignableMembers.map((member) => ({ value: member.user_id, label: `${member.name} (${member.email})` })),
          ]}
        />
        <SelectField
          aria-label="Event role"
          value={role}
          onChange={(event) => setRole(event.target.value as EventOrganizerRole)}
          options={['viewer', 'manager', 'admin'].map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }))}
        />
        <Button onClick={() => void save()} disabled={!memberId}>Save access</Button>
      </div>

      <div className="rounded-2xl border border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-gray-400"><tr><th className="text-left p-3">Member</th><th className="text-left p-3">Event role</th><th className="text-right p-3">Action</th></tr></thead>
          <tbody>{organizers.map((organizer) => <tr key={organizer.user_id} className="border-t border-gray-800">
            <td className="p-3"><p className="font-medium text-white">{organizer.name}</p><p className="text-xs text-gray-500">{organizer.email}</p></td>
            <td className="p-3 capitalize text-gray-300">{organizer.role}</td>
            <td className="p-3 text-right"><Button size="sm" variant="danger" onClick={() => setTarget(organizer)}>Remove</Button></td>
          </tr>)}</tbody>
        </table>
      </div>
    </>}
    <ConfirmDialog open={!!target} onClose={() => setTarget(null)} onConfirm={() => void remove()} title="Remove event access?" description="This person will no longer have delegated access to this event." intent="danger" confirmLabel="Remove" />
  </div>;
}
